/**
 * Server-synced player state.
 *
 * Authority: the server (`progress` table, account-keyed). IndexedDB holds a mirror so a reload
 * paints instantly and a dropped connection doesn't lose the last few dial turns; on reconnect
 * the local copy is pushed and, if the server moved on (another device), the server copy wins
 * and is adopted.
 *
 * Writes are debounced — the dial fires continuously and each turn must not become a request.
 */
import { api, ApiError } from '../api/client.js';
import type { ProgressSummary } from '../api/types.js';
import {
  emptyInvestigation,
  type InvestigationData,
  type LogEntry,
} from '../story/investigation.js';
import { idb } from './idb.js';
import { Store } from './store.js';

export const PROGRESS_SCHEMA_VERSION = 1;

export type EffectsIntensity = 'full' | 'reduced' | 'off';

export interface ProgressData {
  schemaVersion: number;
  /** Where the dial was left, in kHz. */
  frequencyKhz: number;
  settings: {
    masterVolume: number;
    effects: EffectsIntensity;
    /** Chapter 5 uses this; stored from Ch1 so the shape never has to change. */
    muteGhosts: boolean;
    /* --- Chapter 7 accessibility ------------------------------------------------------------ */
    /** Describe non-speech audio on screen. Off by default; opt-in, not imposed. */
    captions: boolean;
    /**
     * 0 = full radio character on voices, 1 = as plain as the chain gets.
     *
     * Not a volume control: it opens the communications-band filter and eases the saturation, so
     * a player who finds speech hard to follow can keep the atmosphere and still catch the words.
     */
    voiceClarity: number;
    /** How much atmospheric crackle, 0–1. Sharp transients are a real accessibility concern. */
    atmospherics: number;
  };
  /** Bands where something was heard. One entry per signal, not per hearing. */
  frequencyLog: LogEntry[];
  /**
   * The story half of the save: clues held, events fired, where each conversation was left, and
   * the ending reached. Absent on a save written before Chapter 3, which `migrateProgress()` fills in.
   */
  investigation: InvestigationData;
  /** Free-form flags. Kept for content that needs a boolean the story schema does not model. */
  flags: Record<string, boolean>;
  /**
   * The operator's card (Chapter 7).
   *
   * `seen` flips the moment the set is first switched on, so a returning player is never taught
   * anything again — including on another machine, since this rides the same server-synced save.
   * `dismissed` records that they turned the card over, which is deliberately *not* the same as
   * having worked the set out; conflating the two would lose the difference between a player who
   * finished the card and one who did not want it.
   */
  onboarding: {
    seen: boolean;
    dismissed: boolean;
  };
  /**
   * The world's own clock. Game time accumulates only while the player is actually listening
   * (see world/clock.ts), so this is the total the world has lived through — not a wall-clock
   * delta. Persisting it is what lets someone come back a week later and find the night where
   * they left it.
   */
  world: {
    elapsedGameMs: number;
  };
  lastPlayedAt: string | null;
}

export type SyncState = 'idle' | 'syncing' | 'synced' | 'offline' | 'conflict';

const CACHE_KEY = (accountId: string) => `progress:${accountId}`;
const DEBOUNCE_MS = 900;

export function defaultProgress(): ProgressData {
  return {
    schemaVersion: PROGRESS_SCHEMA_VERSION,
    frequencyKhz: 5300,
    settings: {
      masterVolume: 0.7,
      effects: 'full',
      muteGhosts: false,
      captions: false,
      voiceClarity: 0,
      atmospherics: 1,
    },
    frequencyLog: [],
    investigation: emptyInvestigation(),
    flags: {},
    onboarding: { seen: false, dismissed: false },
    world: { elapsedGameMs: 0 },
    lastPlayedAt: null,
  };
}

/**
 * Whether the live autosave represents a run worth protecting before it is discarded.
 *
 * Used to decide whether "New Game" needs a confirm: a fresh account (autosave still equal to
 * `defaultProgress()`) starts a new game with no prompt, while any night that has actually been
 * touched — the dial moved, the set switched on, time elapsed, a clue held — takes a confirm first.
 * The `settings` block is ignored on purpose: turning the volume down before playing is not a run.
 */
export function isRunInProgress(data: ProgressData): boolean {
  const fresh = defaultProgress();
  if (data.lastPlayedAt !== null) return true;
  if (data.frequencyKhz !== fresh.frequencyKhz) return true;
  if (data.frequencyLog.length > 0) return true;
  if (data.world.elapsedGameMs > 0) return true;
  if (data.onboarding.seen) return true;
  if (data.investigation.clues.length > 0) return true;
  if (data.investigation.events.length > 0) return true;
  if (Object.keys(data.investigation.dialogue).length > 0) return true;
  if (Object.keys(data.flags).length > 0) return true;
  return false;
}

/** Whether a save shows any sign of having been played, for pre-Chapter-7 blobs. */
function hasPlayed(value: Partial<ProgressData>): boolean {
  if (value.lastPlayedAt) return true;
  if ((value.frequencyLog?.length ?? 0) > 0) return true;
  return (value.world?.elapsedGameMs ?? 0) > 0;
}

/**
 * Fill in anything a newer build added, so an old cached blob is never fatal.
 *
 * Exported because it is the one piece of the sync layer that is pure, and the one most likely
 * to lose someone's progress if it is wrong — a save written by any previous chapter has to come
 * back intact, and that is worth testing directly rather than through a network round-trip.
 */
export function migrateProgress(raw: unknown): ProgressData {
  const base = defaultProgress();
  if (!raw || typeof raw !== 'object') return base;
  const value = raw as Partial<ProgressData>;
  return {
    ...base,
    ...value,
    schemaVersion: PROGRESS_SCHEMA_VERSION,
    settings: { ...base.settings, ...(value.settings ?? {}) },
    frequencyLog: Array.isArray(value.frequencyLog) ? value.frequencyLog : base.frequencyLog,
    // A Chapter 1/2 save has no investigation block; it starts one rather than being rejected.
    investigation: {
      ...base.investigation,
      ...(value.investigation ?? {}),
      clues: Array.isArray(value.investigation?.clues) ? value.investigation.clues : [],
      events: Array.isArray(value.investigation?.events) ? value.investigation.events : [],
      dialogue:
        typeof value.investigation?.dialogue === 'object' && value.investigation.dialogue
          ? value.investigation.dialogue
          : {},
    },
    flags: typeof value.flags === 'object' && value.flags ? value.flags : base.flags,
    // A save written before Chapter 7 has no card state. Such a player has already played, so
    // they are treated as having seen it — showing a tutorial to someone mid-investigation would
    // be worse than never showing it at all.
    onboarding: value.onboarding
      ? { ...base.onboarding, ...value.onboarding }
      : { seen: hasPlayed(value), dismissed: false },
    world: { ...base.world, ...(value.world ?? {}) },
  };
}

interface CacheEntry {
  revision: number | null;
  data: ProgressData;
  dirty: boolean;
}

export class ProgressSync {
  readonly progress = new Store<ProgressData>(defaultProgress());
  readonly syncState = new Store<SyncState>('idle');

  /**
   * Supplies the queryable projection sent with each write. Set by the story layer once a pack is
   * loaded; without it, saves still work and the columns simply stay as they were.
   */
  summaryProvider: (() => ProgressSummary) | null = null;

  private revision: number | null = null;
  private dirty = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;

  constructor(private readonly accountId: string) {}

  /** Paint from cache first, then reconcile with the server. */
  async load(): Promise<void> {
    const cached = await idb.get<CacheEntry>(CACHE_KEY(this.accountId));
    if (cached) {
      this.revision = cached.revision;
      this.dirty = cached.dirty;
      this.progress.set(migrateProgress(cached.data));
    }

    try {
      const remote = await api.getProgress();
      const remoteEmpty = Object.keys(remote.data).length === 0;

      if (remoteEmpty && cached) {
        // Fresh account (or wiped server row) with a local copy — push what we have.
        this.revision = remote.revision;
        this.dirty = true;
        await this.flush();
      } else if (this.dirty && cached && this.revision === remote.revision) {
        // We hold unsynced local edits on top of the same revision — ours wins.
        await this.flush();
      } else {
        this.revision = remote.revision;
        this.dirty = false;
        this.progress.set(migrateProgress(remote.data));
        await this.writeCache();
        this.syncState.set('synced');
      }
    } catch (err) {
      this.syncState.set(err instanceof ApiError ? 'idle' : 'offline');
    }
  }

  /** Apply a change locally (instant), cache it, and schedule a server write. */
  patch(mutate: (current: ProgressData) => ProgressData): void {
    this.progress.update((current) => mutate({ ...current }));
    this.dirty = true;
    void this.writeCache();
    this.schedule();
  }

  /**
   * Replace the live autosave with a loaded save-slot blob. Used when the player loads a manual
   * slot from the Main Menu: the snapshot becomes the active game, so "Continue" thereafter
   * resumes from it. Migrated for safety, marked dirty, and flushed so the server autosave adopts
   * it immediately (the slot itself is untouched — this only overwrites `progress`).
   */
  async adopt(data: unknown): Promise<void> {
    this.progress.set(migrateProgress(data));
    this.dirty = true;
    await this.writeCache();
    await this.flush();
  }

  /**
   * Discard the live autosave and start fresh. Used by New Game and Abandon: the current run is
   * replaced with a clean `defaultProgress()`, marked dirty, and flushed so the server autosave
   * adopts the reset immediately and "Continue" thereafter starts a new night. The four manual
   * slots are untouched — this only overwrites `progress`. Mirrors `adopt()` semantics.
   */
  async reset(): Promise<void> {
    this.progress.set(defaultProgress());
    this.dirty = true;
    await this.writeCache();
    await this.flush();
  }

  private schedule(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), DEBOUNCE_MS);
  }

  /** Push pending local state to the server now. Safe to call repeatedly. */
  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.dirty) return;
    if (this.inFlight) return this.inFlight;

    this.syncState.set('syncing');
    this.inFlight = (async () => {
      try {
        const response = await api.putProgress(
          this.progress.get() as unknown as Record<string, unknown>,
          this.revision,
          this.summaryProvider?.(),
        );
        this.revision = response.revision;
        this.dirty = false;
        this.syncState.set('synced');
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          // Another device advanced past us: adopt the server copy rather than clobber it.
          const body = err.body as { revision: number; data: Record<string, unknown> };
          this.revision = body.revision;
          this.dirty = false;
          this.progress.set(migrateProgress(body.data));
          this.syncState.set('conflict');
        } else if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          this.syncState.set('idle');
        } else {
          this.syncState.set('offline');
        }
      } finally {
        await this.writeCache();
        this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  private async writeCache(): Promise<void> {
    const entry: CacheEntry = {
      revision: this.revision,
      data: this.progress.get(),
      dirty: this.dirty,
    };
    await idb.set(CACHE_KEY(this.accountId), entry);
  }
}
